import React from 'react';
import { Runbook, ViewState } from '../types';

interface HomeViewProps {
  onNavigate: (view: ViewState) => void;
  onOpenDetail: (slug: string) => void;
  featuredRunbook?: Runbook;
}

export const HomeView: React.FC<HomeViewProps> = ({
  onNavigate,
  onOpenDetail,
  featuredRunbook,
}) => {
  return (
    <div className="space-y-16">
      {/* Hero Section */}
      <div className="relative rounded-2xl border border-[#232736] bg-[#10121a] p-6 sm:p-10 lg:p-12 overflow-hidden grid-bg">
        <div className="relative z-10 max-w-2xl space-y-6">
          <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded border border-sky-500/30 bg-sky-500/10 text-[11px] font-mono text-sky-400">
            <span className="w-1.5 h-1.5 rounded-full bg-sky-400 animate-pulse" />
            FREEMIUM MICRO-UTILITIES &amp; SPRINT PROTOCOLS
          </div>
          <h1 className="font-tech text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-[1.1]">
            Operating tools for{' '}
            <span className="text-sky-400 font-normal">independent builders.</span>
          </h1>
          <p className="text-[#8b92a5] text-base sm:text-lg leading-relaxed">
            Stop improvising pricing, client defense, and contracts. Build custom 3-tier rate cards, change orders, executive audit teardowns, and plain-English SOWs in 60 seconds.
          </p>
          <div className="flex flex-wrap gap-3 pt-2">
            <button
              onClick={() => onNavigate('tools')}
              className="px-5 py-3 bg-sky-400 hover:bg-sky-300 text-black font-tech font-bold text-sm rounded shadow-[0_0_20px_rgba(56,189,248,0.3)] active:scale-[0.98] transition flex items-center gap-2"
            >
              <span>⚡ LAUNCH MICRO-TOOLS</span>
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-black/20 text-black font-mono">NEW</span>
            </button>
            <button
              onClick={() => onNavigate('catalog')}
              className="px-5 py-3 border border-[#3b4259] text-zinc-300 hover:text-white font-mono text-xs rounded hover:bg-[#181b26] active:scale-[0.98] transition flex items-center gap-1.5"
            >
              BROWSE RUNBOOKS &rarr;
            </button>
          </div>
        </div>

        {/* Metric Callouts */}
        <div className="relative z-10 grid grid-cols-3 gap-4 border-t border-[#232736] pt-8 mt-10 max-w-lg">
          <div>
            <div className="font-mono text-2xl font-bold text-white">4</div>
            <div className="text-[11px] font-mono text-[#8b92a5] uppercase">Live Micro-Tools</div>
          </div>
          <div>
            <div className="font-mono text-2xl font-bold text-white">&lt; 60s</div>
            <div className="text-[11px] font-mono text-[#8b92a5] uppercase">Generation Time</div>
          </div>
          <div>
            <div className="font-mono text-2xl font-bold text-sky-400">100%</div>
            <div className="text-[11px] font-mono text-[#8b92a5] uppercase">Vector PDF Export</div>
          </div>
        </div>
      </div>

      {/* 4 Micro-Tools Showcase Grid */}
      <div className="space-y-6">
        <div className="flex items-center justify-between border-b border-[#232736] pb-4">
          <div>
            <span className="text-xs font-mono text-sky-400 uppercase tracking-widest">
              THE STUDIO SUITE // AI-ENABLED UTILITIES
            </span>
            <h2 className="font-tech text-2xl sm:text-3xl font-bold text-white mt-1">
              4 Micro-Tools for High-Stakes Client Work
            </h2>
          </div>
          <button
            onClick={() => onNavigate('tools')}
            className="text-xs font-mono text-sky-400 hover:text-sky-300 transition"
          >
            OPEN STUDIO &rarr;
          </button>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div
            onClick={() => onNavigate('tools')}
            className="blueprint-card rounded-xl p-5 border border-[#232736] hover:border-sky-500/50 transition cursor-pointer space-y-3 group"
          >
            <div className="flex items-center justify-between text-xs font-mono">
              <span className="text-sky-400 font-bold">TOOL 01</span>
              <span className="text-[10px] text-zinc-500">PRICING</span>
            </div>
            <h3 className="font-tech text-lg font-bold text-white group-hover:text-sky-300 transition">
              Rate Card Studio
            </h3>
            <p className="text-xs font-mono text-[#8b92a5] leading-relaxed">
              Calculate non-negotiable floor day rates and output 3-tier sprint menus in vector PDF.
            </p>
          </div>

          <div
            onClick={() => onNavigate('tools')}
            className="blueprint-card rounded-xl p-5 border border-[#232736] hover:border-amber-500/50 transition cursor-pointer space-y-3 group"
          >
            <div className="flex items-center justify-between text-xs font-mono">
              <span className="text-amber-400 font-bold">TOOL 02</span>
              <span className="text-[10px] text-zinc-500">DEFENSE</span>
            </div>
            <h3 className="font-tech text-lg font-bold text-white group-hover:text-amber-300 transition">
              Scope Shield
            </h3>
            <p className="text-xs font-mono text-[#8b92a5] leading-relaxed">
              Paste client scope creep messages to compile zero-apology replies and formal Change Order PDFs.
            </p>
          </div>

          <div
            onClick={() => onNavigate('tools')}
            className="blueprint-card rounded-xl p-5 border border-[#232736] hover:border-red-500/50 transition cursor-pointer space-y-3 group"
          >
            <div className="flex items-center justify-between text-xs font-mono">
              <span className="text-red-400 font-bold">TOOL 03</span>
              <span className="text-[10px] text-zinc-500">AUDITS</span>
            </div>
            <h3 className="font-tech text-lg font-bold text-white group-hover:text-red-300 transition">
              Teardown Lab
            </h3>
            <p className="text-xs font-mono text-[#8b92a5] leading-relaxed">
              Compile 3-page executive architecture audits with risk matrices to pitch $10k+ advisory sprints.
            </p>
          </div>

          <div
            onClick={() => onNavigate('tools')}
            className="blueprint-card rounded-xl p-5 border border-[#232736] hover:border-emerald-500/50 transition cursor-pointer space-y-3 group"
          >
            <div className="flex items-center justify-between text-xs font-mono">
              <span className="text-emerald-400 font-bold">TOOL 04</span>
              <span className="text-[10px] text-zinc-500">CONTRACTS</span>
            </div>
            <h3 className="font-tech text-lg font-bold text-white group-hover:text-emerald-300 transition">
              Agreement Armor
            </h3>
            <p className="text-xs font-mono text-[#8b92a5] leading-relaxed">
              Generate 2-page Plain-English SOWs and Retainer SLAs with automatic late invoice stop-work riders.
            </p>
          </div>
        </div>
      </div>

      {/* Featured Highlight Card */}
      {featuredRunbook && (
        <div className="blueprint-card rounded-2xl p-6 sm:p-8 border border-sky-500/30 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div className="space-y-2 max-w-xl">
            <div className="text-xs font-mono text-sky-400 uppercase tracking-wider">
              FLAGSHIP PROTOCOL // {featuredRunbook.code}
            </div>
            <h3 className="font-tech text-2xl sm:text-3xl font-bold text-white">
              {featuredRunbook.title}
            </h3>
            <p className="text-xs font-mono text-[#8b92a5]">
              {featuredRunbook.promise}
            </p>
          </div>
          <button
            onClick={() => onOpenDetail(featuredRunbook.slug)}
            className="px-5 py-3 bg-sky-400 hover:bg-sky-300 text-black font-tech font-bold text-xs rounded transition shrink-0 flex items-center gap-2"
          >
            INSPECT PROTOCOL (${featuredRunbook.price}) &rarr;
          </button>
        </div>
      )}
    </div>
  );
};
